import { AddCircleOutline } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { demeterPageElementsApi, demeterPresentationTemplatesApi } from '../../../Apis/Apis';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import {
    DemeterPageElementModel,
    DemeterPageElementType,
    DemeterPageType,
    PresentationFormat,
    PresentationTemplateSlideModel,
} from '../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../Layouts/NavigationRoutes';
import useApi from '../../Apis/Hooks/useApiHook';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonType } from '../../Components/Form/Buttons/LinkButton';
import TextInput from '../../Components/Form/Inputs/TextInput';
import ComponentSubHeader from '../../Components/Headers/ComponentSubHeader';
import PageHeaderWithBackNavigation from '../../Components/Headers/PageHeaderWithBackNavigation';
import PageLoadingSpinner from '../../Components/LoadingSpinner/PageLoadingSpinner';
import PageElementModal from '../../Components/Modals/PageElementModal/PageElementModal';
import useLanguage from '../../Services/Language/useLanguageHook';
import PresentationTemplateSlidesList from './PresentationTemplateSlidesList';
import styles from './PresentationTemplatesPage.module.scss';

const PresentationTemplateEditPage: React.FC = () => {
    const [translations, translate] = useLanguage();
    const navigate = useNavigate();
    const { presentationTemplateGuid } = useParams();

    const [openSelector, setOpenSelector] = useState(false);

    const [initialName, setInitialName] = useState<string>();
    const [name, setName] = useState<string>();

    const [format, setFormat] = useState<PresentationFormat>(PresentationFormat.Ppt);

    const [initialSlides, setInitialSlides] = useState<PresentationTemplateSlideModel[] | undefined>();
    const [slides, setSlides] = useState<PresentationTemplateSlideModel[] | undefined>();

    const [selectedSlideIndex, setSelectedSlideIndex] = useState<number | null>(null);

    const [currentPresentationTemplateGuid, setCurrentPresentationTemplateGuid] = useState(presentationTemplateGuid ?? null);

    const [, , listDemeterPageElementsResponse] = useApi(() =>
        demeterPageElementsApi.listDemeterPageElementsCurrent(undefined, undefined, undefined, undefined, undefined, 'en-US', true),
    );

    const [, , getPresentationTemplateResponse] = useApi(
        () => {
            if (!currentPresentationTemplateGuid || currentPresentationTemplateGuid === applicationConstants.ZeroGuid) {
                return null;
            }
            return demeterPresentationTemplatesApi.getPresentationTemplate(currentPresentationTemplateGuid!);
        },
        {
            errorMessage: translations.presentationTemplates.errors.getPresentationTemplateError,
        },
    );

    const pageElements = useMemo(() => {
        if (!listDemeterPageElementsResponse || !listDemeterPageElementsResponse.rows) {
            return [];
        }

        return listDemeterPageElementsResponse.rows.filter(
            (pageElement) =>
                pageElement.pageType !== DemeterPageType.Dashboard &&
                pageElement.pageType !== DemeterPageType.MonitorsAndIndicators &&
                pageElement.pageType !== DemeterPageType.NewsAndSummaries &&
                pageElement.pageElementType !== DemeterPageElementType.FuturesPricesTable &&
                pageElement.pageElementType !== DemeterPageElementType.OtcPricesTable &&
                pageElement.pageElementType !== DemeterPageElementType.PhysicalPricesTable,
        );
    }, [listDemeterPageElementsResponse]);

    const normalizeSlidesOrder = (input: PresentationTemplateSlideModel[] | undefined) =>
        (input ?? []).map((s, index) => ({
            ...s,
            order: index,
        }));

    const isValid = !!presentationTemplateGuid && !!name && !!format;

    const [, upsertPresentationTemplate, upsertPresentationTemplateResponse] = useApiWithoutAutoExecute(
        () => {
            if (!isValid || !currentPresentationTemplateGuid) {
                return null;
            }

            const normalized = normalizeSlidesOrder(slides);

            setInitialName(name);
            setInitialSlides(normalized);

            return demeterPresentationTemplatesApi.upsertPresentationTemplate({
                presentationTemplateGuid: currentPresentationTemplateGuid,
                name,
                format,
                rows: normalized,
            } as any);
        },
        {
            successMessage: translations.presentationTemplates.success.updatePresentationTemplateSuccess,
            errorMessage: translations.presentationTemplates.errors.updatePresentationTemplateError,
        },
    );

    useEffect(() => {
        if (!upsertPresentationTemplateResponse || !upsertPresentationTemplateResponse.presentationTemplateGuid) {
            return;
        }
        setCurrentPresentationTemplateGuid(upsertPresentationTemplateResponse.presentationTemplateGuid);
    }, [upsertPresentationTemplateResponse]);

    useEffect(() => {
        const template = getPresentationTemplateResponse?.presentationTemplate;

        setInitialName(template?.name ?? '');
        setName(template?.name ?? '');
        setFormat(template?.format ?? PresentationFormat.Ppt);

        const responseSlides = normalizeSlidesOrder((template as any)?.rows ?? []);
        setInitialSlides(responseSlides);
        setSlides(responseSlides);

        setSelectedSlideIndex(null);
    }, [getPresentationTemplateResponse]);

    const navigateBack = () => {
        navigate(NavigationRoutes.PresentationTemplates);
    };

    const hasAnyChange = name !== initialName || slides !== initialSlides;

    const showLoading = !listDemeterPageElementsResponse || (presentationTemplateGuid !== applicationConstants.ZeroGuid && !getPresentationTemplateResponse);

    const isSaveDisabled = !slides || !isValid || !hasAnyChange;

    const itemFullName = (item: DemeterPageElementModel): string => {
        const {
            pageType,
            dataSource,
            exchange,
            region,
            subRegion,
            commodity,
            subCommodity,
            pageElementType,
            commodityDisplayName,
            subCommodityDisplayName,
            subRegionDisplayName,
            displayName,
        } = item;

        const pageTypeName = pageType && translations.pageType[pageType] ? translations.pageType[pageType] : pageType;
        const dataSourceName = dataSource;
        const exchangeName = exchange && translations.exchange[exchange] ? translations.exchange[exchange] : exchange;
        const regionName = region && translations.region[region] ? translations.region[region] : region;
        const subRegionName = subRegionDisplayName ? translate(subRegionDisplayName) || subRegionDisplayName : subRegion;
        const commodityName = commodityDisplayName ? translate(commodityDisplayName) ?? commodityDisplayName : commodity;
        const subCommodityName = subCommodityDisplayName ? translate(subCommodityDisplayName) || subCommodityDisplayName : subCommodity;
        const pageElementName =
            pageElementType && translations.pageElementType[pageElementType]
                ? translations.pageElementType[pageElementType]
                : (displayName ? translate(displayName) : null) ?? pageElementType ?? '';

        const fullNameParts: string[] =
            ([pageTypeName, dataSourceName, exchangeName, commodityName, subCommodityName, regionName, subRegionName, pageElementName].filter(
                (part) => part !== 'null' && part !== null && part !== undefined,
            ) as string[]) || [];

        return fullNameParts.join(' / ');
    };

    const createEmptySlide = (): PresentationTemplateSlideModel => ({
        presentationTemplateSlideGuid: applicationConstants.ZeroGuid,
        options: {},
        rows: [],
    });

    const addPageElement = (pageElement: DemeterPageElementModel) => {
        setSlides((prev) => {
            const current = prev ?? [];

            if (selectedSlideIndex !== null && current[selectedSlideIndex]) {
                const updated = [...current];
                const slide = updated[selectedSlideIndex];

                updated[selectedSlideIndex] = {
                    ...slide,
                    rows: [...(slide.rows ?? []), pageElement],
                };

                return normalizeSlidesOrder(updated);
            }

            return normalizeSlidesOrder([...current, { ...createEmptySlide(), rows: [pageElement] }]);
        });
    };

    const handleSlidesChange = (next: PresentationTemplateSlideModel[] | undefined) => {
        const normalized = normalizeSlidesOrder(next);
        setSlides(normalized);

        if (selectedSlideIndex === null) {
            return;
        }

        if (!normalized || selectedSlideIndex < 0 || selectedSlideIndex >= normalized.length) {
            setSelectedSlideIndex(null);
        }
    };

    const slideHeader = (index: number) => `Slide ${index + 1}`;

    return showLoading ? (
        <PageLoadingSpinner />
    ) : (
        <div className={styles.master_page_container}>
            <PageHeaderWithBackNavigation
                handleBackNavigation={navigateBack}
                title={translations.presentationTemplates.title.edit}
                track={{ eventCategory: 'PresentationTemplateEdit', eventAction: 'BackClicked' }}
            />

            <div className={styles.presentation_templates_control_panel}>
                <div className={styles.presentation_templates_name}>
                    <TextInput
                        value={name}
                        handleTextChange={setName}
                        placeholder={translations.presentationTemplates.text.namePlaceholder}
                        errorMessage={translations.presentationTemplates.errors.templateNameRequired}
                        required
                        track={{ eventCategory: 'PresentationTemplateEdit', eventAction: 'TemplateNameChanged', eventValue: name }}
                    />
                </div>

                <div className={styles.presentation_templates_actions}>
                    <LinkButton
                        title={translations.presentationTemplates.actions.chooseContent}
                        type={LinkButtonType.White}
                        onClick={() => setOpenSelector(true)}
                        track={{ eventCategory: 'PresentationTemplateEdit', eventAction: 'ChooseContentClicked' }}
                    />
                    <LinkButton
                        title={translations.presentationTemplates.actions.save}
                        type={LinkButtonType.Blue}
                        disabled={isSaveDisabled}
                        onClick={upsertPresentationTemplate}
                        track={{ eventCategory: 'PresentationTemplateEdit', eventAction: 'SaveClicked' }}
                    />
                </div>
            </div>

            <ComponentSubHeader title={translations.presentationTemplates.text.templateContentHeader} />

            <div className={styles.presentation_templates_content}>
                <div className={styles.presentation_templates_content_order}>
                    <PresentationTemplateSlidesList
                        slides={slides}
                        allowDelete
                        height={500}
                        renderSlideHeader={(slide, slideIndex) => <div>{slideHeader(slideIndex)}</div>}
                        renderItem={(item: DemeterPageElementModel) => <div>{itemFullName(item)}</div>}
                        emptyContent={
                            <button type="button" className={styles.presentation_templates_content_order_empty_button} onClick={() => setOpenSelector(true)}>
                                <span className={styles.presentation_templates_content_order_empty_icon}>
                                    <AddCircleOutline />
                                </span>
                                {translations.presentationTemplates.actions.chooseContent}
                            </button>
                        }
                        onChange={handleSlidesChange}
                        onAddItem={() => setOpenSelector(true)}
                        onSelectSlide={(idx) => setSelectedSlideIndex(idx)}
                        onSelectItem={(idx) => setSelectedSlideIndex(idx)}
                        onUnselect={() => setSelectedSlideIndex(null)}
                    />
                </div>
            </div>

            <PageElementModal
                title={translations.words.addContent}
                pageElements={pageElements}
                open={openSelector}
                handleClose={() => setOpenSelector(false)}
                handleAdd={addPageElement}
                allowMultipleSelection
                track={{ eventCategory: 'PresentationTemplateEdit', eventAction: 'ModalChooseContent' }}
            />
        </div>
    );
};

export default PresentationTemplateEditPage;
